/**
 * Reusable client-side pagination component.
 *
 * Usage:
 *   const { page, pageSize, setPage, setPageSize, total, pageItems } = usePagination(rows, 20);
 *   <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />
 */
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

export function usePagination(items = [], defaultPageSize = 20) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const total = items.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const pageItems = useMemo(
        () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
        [items, safePage, pageSize]
    );
    return {
        page: safePage,
        pageSize,
        total,
        totalPages,
        pageItems,
        setPage,
        setPageSize: (n) => {
            setPageSize(n);
            setPage(1);
        },
    };
}

export default function Pagination({
    page = 1,
    pageSize = 20,
    total = 0,
    onPage,
    onPageSize,
    pageSizeOptions = [10, 20, 50, 100],
    className = "",
}) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);

    const btn = (disabled, onClick, children, testId) => (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            data-testid={testId}
            className="h-8 w-8 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
        >
            {children}
        </button>
    );

    return (
        <div
            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 ${className}`}
            data-testid="pagination"
        >
            <div className="flex items-center gap-2 text-xs font-mono-ui text-[#6B645C]">
                <span data-testid="pagination-range">
                    {start}–{end} of {total}
                </span>
                <span className="opacity-40">·</span>
                <label className="flex items-center gap-1.5">
                    Rows
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSize?.(parseInt(e.target.value, 10))}
                        className="bg-white border border-[#E7DED2] rounded-full px-2 py-1 text-xs"
                        data-testid="pagination-page-size"
                    >
                        {pageSizeOptions.map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="flex items-center gap-1.5">
                {btn(page <= 1, () => onPage?.(1), <ChevronsLeft className="h-3.5 w-3.5" />, "pagination-first")}
                {btn(page <= 1, () => onPage?.(page - 1), <ChevronLeft className="h-3.5 w-3.5" />, "pagination-prev")}
                <span className="text-xs font-mono-ui px-3" data-testid="pagination-current">
                    {page} / {totalPages}
                </span>
                {btn(page >= totalPages, () => onPage?.(page + 1), <ChevronRight className="h-3.5 w-3.5" />, "pagination-next")}
                {btn(page >= totalPages, () => onPage?.(totalPages), <ChevronsRight className="h-3.5 w-3.5" />, "pagination-last")}
            </div>
        </div>
    );
}
